import { CacheDuration } from "@solomon-labs/caching";
import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { create } from "zustand";

import { readSolomonE2EChainOverride } from "../wallet/e2e";
import type { BalanceOverrideState, TokenBalanceDelta, TokenBalanceKey, TokenBalances } from "../wallet/types/balances";
import { INITIAL_BALANCES } from "../wallet/types/balances";
import { fetchSPLTokenAccountsFallback } from "../wallet/utils/balanceFetcher";
import { setFetchedBalances } from "../wallet/utils/balanceReconciliation";
import { parseMockBalance, parseTokenBalances } from "../wallet/utils/parseTokenBalances";

export type { BalanceOverrideState, TokenBalanceDelta, TokenBalanceKey, TokenBalances };

interface WalletState {
    balances: TokenBalances;
    balanceOverride: BalanceOverrideState | null;
    balanceMutationLockId: number | null;
    balanceMutationLockReason: string | null;
    nextBalanceMutationLockId: number;
    isLoadingBalances: boolean;
    isReconcilingBalances: boolean;
    balanceError: string | null;
    lastFetchedAt: number | null;
    lastMutationAt: number | null;
    mutationVersion: number;
    /** Track which address we last fetched for (prevents duplicate fetches) */
    lastFetchedAddress: string | null;
    /** Track if a fetch is currently in progress */
    isFetchingBalances: boolean;
    /** AbortController for cancelling in-progress fetches */
    abortController: AbortController | null;
    fetchBalances: (owner: PublicKey, force?: boolean) => Promise<void>;
    beginBalanceMutation: (reason: string) => number | null;
    endBalanceMutation: (lockId: number) => void;
    setBalanceOverride: (expectedBalances: TokenBalances, preSwapBalances: TokenBalances) => number;
    clearBalanceOverride: (expectedVersion?: number) => void;
    setReconcilingBalances: (isReconciling: boolean) => void;
    markBalancesStale: () => void;
    clearBalances: () => void;
}

export const useWalletStore = create<WalletState>((set, get) => ({
    balances: INITIAL_BALANCES,
    balanceOverride: null,
    balanceMutationLockId: null,
    balanceMutationLockReason: null,
    nextBalanceMutationLockId: 1,
    isLoadingBalances: false,
    isReconcilingBalances: false,
    balanceError: null,
    lastFetchedAt: null,
    lastMutationAt: null,
    mutationVersion: 0,
    lastFetchedAddress: null,
    isFetchingBalances: false,
    abortController: null,

    beginBalanceMutation: (reason: string): number | null => {
        const state = get();
        if (state.balanceMutationLockId !== null) {
            return null;
        }

        const lockId = state.nextBalanceMutationLockId;
        set({
            balanceMutationLockId: lockId,
            balanceMutationLockReason: reason,
            nextBalanceMutationLockId: lockId + 1
        });

        return lockId;
    },

    endBalanceMutation: (lockId: number): void => {
        const state = get();
        if (state.balanceMutationLockId !== lockId) {
            return;
        }

        set({
            balanceMutationLockId: null,
            balanceMutationLockReason: null
        });
    },

    fetchBalances: async (owner: PublicKey, force = false): Promise<void> => {
        const state = get();
        const ownerAddress = owner.toString();

        const e2eChainOverride = readSolomonE2EChainOverride();
        if (e2eChainOverride?.balances) {
            const mockBalances: TokenBalances = {
                usdv: parseMockBalance(e2eChainOverride.balances.usdv),
                legacyUsdv: parseMockBalance(e2eChainOverride.balances.legacyUsdv),
                usdc: parseMockBalance(e2eChainOverride.balances.usdc),
                usdt: parseMockBalance(e2eChainOverride.balances.usdt),
                susdv: parseMockBalance(e2eChainOverride.balances.susdv),
                solo: parseMockBalance(e2eChainOverride.balances.solo)
            };
            setFetchedBalances(set, mockBalances, ownerAddress);
            return;
        }

        // Guard: Already fetching - skip non-forced requests
        // For force=true, cancel in-progress fetch and start fresh
        if (state.isFetchingBalances) {
            if (!force) {
                return;
            }
            // Cancel in-progress fetch for forced refresh
            state.abortController?.abort();
        }

        // Guard: Already fetched for this address (unless forced)
        if (!force && state.lastFetchedAddress === ownerAddress) {
            return;
        }

        // Create new AbortController for this fetch
        const abortController = new AbortController();

        set({
            isLoadingBalances: true,
            isFetchingBalances: true,
            balanceError: null,
            abortController
        });

        try {
            // Note: ConnectionManager may log a BigInt serialization error when caching
            // This is a known issue in @solomon-labs/caching - the fetch still works
            // Bust cache when force=true to ensure fresh data after transactions
            const cacheOptions = force ? { cacheDuration: CacheDuration.Bust } : undefined;
            const { allAccounts } = await ConnectionManager.getTokenAccountsForOwner(owner, cacheOptions);

            // Check if this fetch was aborted (wallet switched during fetch)
            if (abortController.signal.aborted) {
                return;
            }

            const balances = parseTokenBalances(
                allAccounts.map((a) => ({ mint: a.mint.toString(), amount: a.amount.toString() }))
            );

            // Final abort check before updating state
            if (abortController.signal.aborted) {
                return;
            }

            setFetchedBalances(set, balances, ownerAddress);
        } catch (error) {
            // Don't update state if fetch was aborted
            if (abortController.signal.aborted) {
                return;
            }

            // Check if this is a Token-2022 decoding error - if so, try fallback
            const isToken2022Error =
                error instanceof Error &&
                (error.message.includes("Token-2022") || error.constructor.name === "AccountDecodingError");

            if (isToken2022Error) {
                try {
                    const splAccounts = await fetchSPLTokenAccountsFallback(ownerAddress);

                    if (abortController.signal.aborted) {
                        return;
                    }

                    const balances = parseTokenBalances(splAccounts);

                    setFetchedBalances(set, balances, ownerAddress);
                    return;
                } catch (_fallbackError) {
                    // Fallback also failed, continue to show original error
                }
            }

            const errorMessage = error instanceof Error ? error.message : "Failed to fetch balances";
            set({
                balanceError: errorMessage,
                isLoadingBalances: false,
                isFetchingBalances: false,
                abortController: null
            });
        }
    },

    setBalanceOverride: (expectedBalances: TokenBalances, preSwapBalances: TokenBalances): number => {
        const nextVersion = get().mutationVersion + 1;

        set({
            balanceOverride: { expectedBalances, preSwapBalances, version: nextVersion },
            mutationVersion: nextVersion,
            lastMutationAt: Date.now()
        });
        return nextVersion;
    },

    clearBalanceOverride: (expectedVersion?: number): void => {
        const state = get();
        if (expectedVersion !== undefined && state.balanceOverride?.version !== expectedVersion) {
            return;
        }

        set({ balanceOverride: null });
    },

    setReconcilingBalances: (isReconciling: boolean): void => {
        set({ isReconcilingBalances: isReconciling });
    },

    markBalancesStale: (): void => {
        set({
            lastMutationAt: Date.now()
        });
    },

    clearBalances: (): void => {
        const state = get();
        // Cancel any in-progress fetch
        state.abortController?.abort();

        set({
            balances: INITIAL_BALANCES,
            balanceOverride: null,
            balanceMutationLockId: null,
            balanceMutationLockReason: null,
            nextBalanceMutationLockId: 1,
            isLoadingBalances: false,
            isReconcilingBalances: false,
            balanceError: null,
            lastFetchedAt: null,
            lastMutationAt: null,
            mutationVersion: 0,
            lastFetchedAddress: null,
            isFetchingBalances: false,
            abortController: null
        });
    }
}));
